/**
 * The wire vocabulary, mirroring `backend/event_mapper.py`.
 *
 * Kept deliberately loose at the edges (`[key: string]: unknown` on the agent's
 * own payloads) so that a new field on the Python side does not need a change
 * here before it can be read.
 */

export const STAGES = [
  'Label',
  'Provenance',
  'Market',
  'Palate',
  'Vintage',
  'Cellaring',
] as const

export type Stage = (typeof STAGES)[number]

export interface BottleCard {
  slotId: string
  bottleId: string | null
  cropUri: string | null
  cropUrl?: string
  producerGuess?: string | null
  cuveeGuess?: string | null
  vintageGuess?: string | number | null
  colour?: string | null
  legibility?: string | null
  stages: Stage[]
}

export interface WineMatch {
  slotId: string
  wineId: string
  displayName?: string
  isNew: boolean
  matchMethod?: string
  priorSightings?: { seen_at?: string; scan_run_id?: string }[]
}

/** Mirrors `app.schemas.AxisScores`. All 0–100. */
export interface AxisScores {
  quality: number
  value: number
  drink_now: number
  versatility: number
}

/** Mirrors `app.schemas.RankedEntry`. `slotId` is added by the API. */
export interface RankedEntry {
  rank: number
  slotId?: string
  state_key?: string
  bottle_id: string
  wine_id?: string | null
  display_name: string
  total_score: number
  axis_scores: AxisScores
  contributions?: Record<string, number>
  crop_uri?: string | null
  cropUrl?: string
  match_method?: string | null
  is_new?: boolean
  prior_sightings?: { seen_at: string; scan_run_id?: string }[]
  /** Bottles of this wine already in your cellar. Only set on a shopping scan,
   *  and null there means "not in your cellar", which is not nought. */
  owned?: number | null
}

/** A price read off the rail in a shop, rather than found on the web. */
export interface ShelfTicket {
  amount: number
  currency: string
  note?: string | null
}

export interface SourcedClaim {
  value: string
  source_url: string
  source_name: string
}

export interface CriticScore {
  score: string
  critic: string
  source_url: string
}

export interface PriceObservation {
  amount: number
  currency: string
  retailer: string
  source_url: string
}

/** Mirrors `app.schemas.BottleDossier` — the evidence behind one ranked row. */
export interface Dossier {
  bottle_id: string
  display_name: string
  producer?: string | null
  cuvee?: string | null
  vintage?: number | null
  region?: string | null
  appellation?: string | null
  country?: string | null
  grapes?: string[]
  colour?: string
  abv?: number | null
  palate?: {
    body: number
    tannin: number
    acidity: number
    sweetness: number
    oak: number
    primary_flavours: string[]
    serving_temp_c: string
    decant_minutes: number
  }
  axis_scores?: AxisScores
  summary?: string
  style_note?: string
  critic_scores?: CriticScore[]
  price_points?: PriceObservation[]
  shelf_price?: ShelfTicket | null
  drinking_window?: SourcedClaim | null
  provenance_notes?: SourcedClaim[]
  evidence_level?: 'sourced' | 'inferred' | 'unsourced'
  caveats?: string[]
}

export interface PairingGuide {
  menu_theme?: string
  suggestions?: { course: string; dish: string; bottle_id: string; rationale: string }[]
  serving_order?: string[]
}

export interface ScanResult {
  run_id: string
  persona_id?: string
  research_depth?: string
  mode?: string
  user_context?: string
  ranking_weights?: Record<string, number>
  bottles_detected?: number
  bottles_legible?: number
  ranking: RankedEntry[]
  dossiers?: Dossier[]
  pairing_guide?: PairingGuide | null
  verdict?: string
}

export type ProgressEvent =
  | { type: 'stream_open'; runId: string }
  | { type: 'bottles_detected'; count: number; distinct: number; sceneNotes?: string }
  | { type: 'selection'; kept?: number; skipped?: { reason?: string }[] }
  | { type: 'bottles_ready'; bottles: BottleCard[] }
  | { type: 'branch_started'; slotId: string; bottleId?: string; stage: Stage }
  | { type: 'branch_done'; slotId: string; bottleId?: string; stage: Stage }
  | ({ type: 'wine_matched' } & WineMatch)
  | { type: 'bottle_collapsed'; slotId: string; intoSlotId: string }
  | { type: 'ranking_ready'; ranking: RankedEntry[] }
  | { type: 'pairing_ready'; pairingGuide: PairingGuide }
  | { type: 'verdict_delta'; text: string }
  | { type: 'progress'; author: string; text: string }
  | { type: 'progress_snapshot'; status?: string; phase?: string; cards?: BottleCard[] }
  // Arrives after `run_done` on a shopping scan: the four critics are asked
  // over the finished ranking, which is a few seconds behind the verdict.
  | { type: 'shelf_answers'; answers: PanelAnswer[] }
  | { type: 'run_done'; result: ScanResult }
  | { type: 'run_failed'; error: string }

export interface ScanDocument {
  runId: string
  userId: string
  status: 'pending' | 'running' | 'complete' | 'failed'
  phase?: string
  personaId?: string
  researchDepth?: string
  mode?: string
  context?: string
  rankingWeights?: Record<string, number>
  weightingNote?: string
  shelfAnswers?: PanelAnswer[]
  createdAt?: string
  completedAt?: string
  error?: string
  bottlesDetected?: number
  sceneNotes?: string
  cards?: BottleCard[]
  matched?: Record<string, WineMatch>
  collapsed?: Record<string, string>
  ranking?: RankedEntry[]
  pairingGuide?: PairingGuide
  result?: ScanResult
  images?: { imageId: string; uri: string; originalUri: string; seenAt?: string }[]
}

export interface ScanCreated {
  runId: string
  personaId: string
  researchDepth: string
  mode: string
  context: string
  rankingWeights: Record<string, number>
  weightingNote: string
  uploads: {
    imageId: string
    seenAt: string | null
    analysis: SignedTarget
    original: SignedTarget
  }[]
}

export interface SignedTarget {
  url: string
  gsUri: string
  contentType: string
  /**
   * The size cap, signed into the URL as `1,<maxUploadBytes>`. It is listed in
   * the signature's `X-Goog-SignedHeaders`, so the PUT has to carry it back
   * verbatim — see `uploadSigned`.
   */
  'x-goog-content-length-range': string
}

/** A row of `users/{uid}/cellar`, flattened by the API. */
export interface PalateProfile {
  body?: number
  tannin?: number
  acidity?: number
  sweetness?: number
  oak?: number
  primary_flavours?: string[]
  primaryFlavours?: string[]
  serving_temp_c?: string
  servingTempC?: string
  decant_minutes?: number
  decantMinutes?: number
}

export interface CellarWine {
  wineId: string
  displayName?: string
  producer?: string | null
  cuvee?: string | null
  vintage?: number | string | null
  colour?: string
  region?: string | null
  country?: string | null
  cropUri?: string | null
  cropUrl?: string
  /** Joined onto the row from the shared wine document by `list_cellar`, and
   *  absent for a wine no specialist ever managed to read the grapes off. */
  grapes?: string[]
  abv?: number | null
  sightingCount?: number
  firstSeenAt?: string
  lastSeenAt?: string
  myRating?: number | null
  /** Bottles left. The API defaults an absent count to one, so this is always
   *  a number on the way out even though most documents do not store it. */
  bottleCount?: number
  summary?: string
  styleNote?: string
  palate?: PalateProfile
}

export interface Sighting {
  sightingId: string
  scanRunId: string
  seenAt?: string
  cropUri?: string | null
  cropUrl?: string
  imageOriginalUri?: string | null
  imageUrl?: string
  rank?: number
  score?: number
  oneLiner?: string
  summary?: string
  styleNote?: string
  palate?: PalateProfile
  axisScores?: AxisScores
}

export interface CriticReview {
  reviewId: string
  wineId: string
  userId: string
  personaId: string
  personaName: string
  notes?: string
  text: string
  createdAt: string
}

/**
 * One critic's answer to a question put to the whole cellar.
 *
 * `wineId` is null when the critic picked a bottle that was not on the list —
 * see `_resolve` in `backend/panel.py`. `error` replaces the answer entirely
 * when that one seat failed; the other three still arrive.
 */
/** What the world charges, against what the restaurant is charging. */
export interface MarkupNote {
  retailEstimate?: number | null
  currency: string
  markup?: string | null
  verdict: string
}

export interface PanelAnswer {
  personaId: string
  personaName: string
  lensId: string
  lensName: string
  lensBrief: string
  /**
   * Which line of the list was picked, 1-based. The only handle on a menu pick
   * — a restaurant line has no `wineId`, because it was never researched and
   * is not in the catalogue. See `menu.list_rows`.
   */
  pickNumber?: number | null
  wineId?: string | null
  displayName?: string | null
  headline?: string
  answer?: string
  runnerUpWineId?: string | null
  runnerUpName?: string | null
  /** Menu mode only, and absent when the search could not price the bottle. */
  markup?: MarkupNote | null
  error?: string
}

/** A bottle a question was asked *about*, as it was named on the night. */
export interface QuestionSubject {
  wineId: string
  displayName?: string | null
}

export interface CellarQuestion {
  questionId: string
  userId: string
  question: string
  answers: PanelAnswer[]
  winesConsidered: number
  /** Empty on every question asked before bottles could be named, so it is
   *  optional rather than defaulted — an older document has no field at all. */
  subjects?: QuestionSubject[]
  askedAt: string
}

/** The food critic's reading of the kitchen, shared by all four sommeliers. */
export interface PairingBrief {
  kitchen: string
  likelyOrder: string
  hardConstraints: string[]
  wants: string
  avoid: string
}

export interface MenuDish {
  name: string
  description?: string | null
  course?: string | null
}

/** One line of a restaurant wine list, as transcribed. */
export interface ListedWine {
  wineId: null
  displayName: string
  region?: string | null
  country?: string | null
  colour?: string | null
  grapes?: string[]
  section?: string | null
  listPrice?: { amount: number; currency: string }
  glassPrice?: { amount: number; currency: string }
}

export interface MenuQuestion {
  questionId: string
  menuId: string
  userId: string
  askedAt: string
  notes: string
  currency: string
  winesConsidered: number
  /** How much of the list did not fit in one prompt; 0 almost always. */
  winesTrimmed: number
  transcriptionNote?: string | null
  cuisine?: string | null
  dishes: MenuDish[]
  brief: PairingBrief | null
  wines: ListedWine[]
  answers: PanelAnswer[]
}

export interface WineDetail {
  wine: CellarWine & Record<string, unknown>
  sightings: Sighting[]
  myRating: { stars: number } | null
  /** Null when this wine is not in your cellar — someone else photographed it. */
  bottleCount?: number | null
  comments: {
    commentId: string
    userId: string
    displayName: string
    text: string
    createdAt: string
  }[]
  criticReviews?: CriticReview[]
}

export interface FamilyMember {
  uid: string
  email: string | null
  displayName: string | null
}

export interface Family {
  familyId: string
  inviteCode: string
  isCreator: boolean
  members: FamilyMember[]
}

export interface FamilyResponse {
  family: Family | null
}


